import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import polyline from '@mapbox/polyline';
import 'mapbox-gl/dist/mapbox-gl.css';

// Mapbox access token
const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoiZGFuaWVsa2FsZG9yIiwiYSI6ImNtYjRyNTYzczA1emcya3FwdzhoMmhmdGIifQ.qkeEKxO6j5ugrpbdC06grA';

// Check if mapboxgl is available
if (typeof mapboxgl === 'undefined') {
  console.error('Mapbox GL JS is not loaded!');
} else {
  console.log('Mapbox GL JS version:', mapboxgl.version);
  mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
}

const CAPTURE_RENDER_TIMEOUT = 5000;
const HIGH_DPI_SCALE = 2; // Cap high-DPI rendering to keep capture sizes manageable
const MAJOR_LABEL_LAYER_PATTERNS = [
    'country',
    'state',
    'province',
    'region',
    'settlement-major',
    'place-major',
    'capital'
];
const buildZoomCompensatedLineWidth = (baseLineWidth) => ([
    'interpolate',
    ['linear'],
    ['zoom'],
    8, Math.max(1, baseLineWidth * 2),
    10, Math.max(1, baseLineWidth * 1.45),
    12, Math.max(1, baseLineWidth),
    14, Math.max(1, baseLineWidth)
]);
const shouldKeepMajorLabelLayer = (layer) => {
    const combinedLayerName = `${layer?.id || ''} ${layer?.['source-layer'] || ''}`.toLowerCase();
    return MAJOR_LABEL_LAYER_PATTERNS.some((pattern) => combinedLayerName.includes(pattern));
};
const applyReducedLabelDensity = (mapRef, logPrefix = 'ActivityMap3D') => {
    const layers = mapRef?.getStyle?.()?.layers || [];
    let hiddenSymbolLayerCount = 0;
    let keptMajorLabelLayerCount = 0;

    layers.forEach((layer) => {
        if (layer.type !== 'symbol') return;

        if (shouldKeepMajorLabelLayer(layer)) {
            keptMajorLabelLayerCount += 1;
            return;
        }

        try {
            const visibility = mapRef.getLayoutProperty?.(layer.id, 'visibility');
            if (visibility !== 'none') {
                mapRef.setLayoutProperty(layer.id, 'visibility', 'none');
                hiddenSymbolLayerCount += 1;
            }
        } catch (error) {
            console.warn(`${logPrefix}: Failed to hide symbol layer "${layer.id}"`, error);
        }
    });

    console.log(
        `${logPrefix}: Reduced label density (kept ${keptMajorLabelLayerCount} major label layers, hid ${hiddenSymbolLayerCount} symbol layers)`
    );
};

export const DEFAULT_MAP_CAPTURE_WIDTH = 1536;
export const DEFAULT_MAP_CAPTURE_HEIGHT = 1024;

const ActivityMap3D = ({
    activity,
    width = DEFAULT_MAP_CAPTURE_WIDTH,
    height = DEFAULT_MAP_CAPTURE_HEIGHT,
    displayWidth = null, // Visual display size (if different from render size)
    displayHeight = null, // Visual display size (if different from render size)
    onCapture = null,
    onMapInteraction = null,
    onRenderStart = null,
    onRenderComplete = null,
    style = 'mapbox://styles/samuelcollin3333/cmf525thz00xg01rk0ewt0i6f',
    bearing = 0,
    pitch = 60,
    zoom = null,
    lineWidth = 8,
    highDPI = true // Enable high DPI rendering by default
}) => {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [cameraInfo, setCameraInfo] = useState(null);
    const [hasBeenInteracted, setHasBeenInteracted] = useState(false);
    const [capturedImage, setCapturedImage] = useState(null);
    const capturedImageRef = useRef(null); // Persist captured image across renders
    const captureCallbackRef = useRef(async () => null);
    const mapInteractionCallbackRef = useRef(onMapInteraction);
    const finalRenderResolvedRef = useRef(false);
    const hasBeenInteractedRef = useRef(false);
    const onRenderStartRef = useRef(onRenderStart);
    const onRenderCompleteRef = useRef(onRenderComplete);

    useEffect(() => {
        mapInteractionCallbackRef.current = onMapInteraction;
    }, [onMapInteraction]);

    useEffect(() => {
        onRenderStartRef.current = onRenderStart;
    }, [onRenderStart]);

    useEffect(() => {
        onRenderCompleteRef.current = onRenderComplete;
    }, [onRenderComplete]);


const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    if (!blob) {
        reject(new Error('No blob provided for conversion'));
        return;
    }

    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
});

const captureCanvasBlob = (canvas, type = 'image/png', quality) => new Promise((resolve) => {
    canvas.toBlob((blob) => {
        resolve(blob);
    }, type, quality);
});

const ensureNextRender = useCallback((label = 'capture', timeout = CAPTURE_RENDER_TIMEOUT) => {
        if (!map.current) return Promise.resolve();

        return new Promise((resolve) => {
            let settled = false;
            let timeoutId;

            const renderHandler = () => checkTiles();
            const idleHandler = () => checkTiles();
            const styleHandler = () => checkTiles();
            const sourceHandler = (event) => {
                if (event?.isSourceLoaded) {
                    checkTiles();
                }
            };
            const dataHandler = () => checkTiles();
            const dataloading = () => {
                // Track that data is loading
            };

            const detachListeners = () => {
                const mapRef = map.current;
                if (!mapRef) return;
                mapRef.off('render', renderHandler);
                mapRef.off('idle', idleHandler);
                mapRef.off('styledata', styleHandler);
                mapRef.off('sourcedata', sourceHandler);
                mapRef.off('data', dataHandler);
                mapRef.off('dataloading', dataloading);
            };

            const finish = (reason) => {
                if (settled) return;
                settled = true;
                if (timeoutId) clearTimeout(timeoutId);
                detachListeners();
                console.log(`ActivityMap3D: Render settle complete (${label}${reason ? ` - ${reason}` : ''})`);
                resolve();
            };

            const checkTiles = () => {
                const mapRef = map.current;
                if (!mapRef) return;

                const tilesLoaded = mapRef.areTilesLoaded?.() ?? false;
                const styleLoaded = mapRef.isStyleLoaded?.() ?? false;
                const fullyLoaded = mapRef.loaded?.() ?? false;

                // Additional check for source loading
                let allSourcesLoaded = true;
                const style = mapRef.getStyle?.();
                if (style && style.sources) {
                    for (const sourceId in style.sources) {
                        if (mapRef.isSourceLoaded && !mapRef.isSourceLoaded(sourceId)) {
                            allSourcesLoaded = false;
                            break;
                        }
                    }
                }

                if (tilesLoaded && styleLoaded && fullyLoaded && allSourcesLoaded) {
                    finish('tiles-loaded');
                }
            };

            const mapRef = map.current;
            if (mapRef) {
                mapRef.on('render', renderHandler);
                mapRef.on('idle', idleHandler);
                mapRef.on('styledata', styleHandler);
                mapRef.on('sourcedata', sourceHandler);
                mapRef.on('data', dataHandler);
                mapRef.on('dataloading', dataloading);
            }

            map.current.triggerRepaint();
            checkTiles();

            timeoutId = setTimeout(() => {
                console.warn(`ActivityMap3D: Render settle timeout (${label}), proceeding`);
                finish('timeout');
            }, timeout);
        });
    }, []);

    const scheduleFinalRenderCheck = useCallback(() => {
        if (!map.current || finalRenderResolvedRef.current) return;

        const confirmFinal = () => {
            if (!map.current || finalRenderResolvedRef.current) return;

            const styleLoaded = map.current.isStyleLoaded?.();
            const tilesLoaded = map.current.areTilesLoaded?.();
            const fullyLoaded = map.current.loaded?.();

            if (styleLoaded && tilesLoaded && fullyLoaded) {
                finalRenderResolvedRef.current = true;
                onRenderCompleteRef.current?.();
                return;
            }

            map.current.once('idle', confirmFinal);
        };

        map.current.once('idle', confirmFinal);
    }, []);

    // Decode polyline to coordinates
    const decodePolyline = useCallback(() => {
        if (!activity?.map?.summary_polyline) {
            console.log('ActivityMap3D: No summary_polyline found in activity');
            return null;
        }

        try {
            console.log('ActivityMap3D: Starting polyline decode, polyline length:', activity.map.summary_polyline.length);
            const decoded = polyline.decode(activity.map.summary_polyline);
            console.log('ActivityMap3D: Raw decoded coordinates count:', decoded.length);
            console.log('ActivityMap3D: First 3 raw decoded coords:', decoded.slice(0, 3));

            // Validate coordinates and filter out any invalid ones
            const validCoords = decoded
                .map((coord, idx) => {
                    const swapped = [coord[1], coord[0]]; // Swap lat/lng to lng/lat
                    if (idx < 3) {
                        console.log(`ActivityMap3D: Coord ${idx} - raw: [${coord[0]}, ${coord[1]}], swapped: [${swapped[0]}, ${swapped[1]}]`);
                    }
                    return swapped;
                })
                .filter((coord, idx) => {
                    const isValid = !isNaN(coord[0]) && !isNaN(coord[1]) &&
                                  Math.abs(coord[0]) <= 180 && Math.abs(coord[1]) <= 90;
                    if (!isValid) {
                        console.warn(`ActivityMap3D: Invalid coordinate at index ${idx}:`, coord,
                                   'isNaN checks:', !isNaN(coord[0]), !isNaN(coord[1]));
                    }
                    return isValid;
                });

            if (validCoords.length === 0) {
                console.error('ActivityMap3D: No valid coordinates after filtering');
                return null;
            }

            console.log(`ActivityMap3D: Decoded ${validCoords.length} valid coordinates from polyline`);
            console.log('ActivityMap3D: First valid coord:', validCoords[0]);
            console.log('ActivityMap3D: Last valid coord:', validCoords[validCoords.length - 1]);
            console.log('ActivityMap3D: Middle valid coord:', validCoords[Math.floor(validCoords.length / 2)]);
            return validCoords;
        } catch (error) {
            console.error('Error decoding polyline:', error);
            return null;
        }
    }, [activity]);

    // Initialize map
    // Capture map as image
    const captureMap = useCallback(async () => {
        if (!map.current) return null;

        try {
            console.log('ActivityMap3D: Starting map capture');

            // Get current camera state for debugging
            const center = map.current.getCenter();
            const zoom = map.current.getZoom();
            const bearing = map.current.getBearing();
            const pitch = map.current.getPitch();
            console.log(`ActivityMap3D: Current camera - center: [${center.lng.toFixed(4)}, ${center.lat.toFixed(4)}], zoom: ${zoom.toFixed(1)}, bearing: ${bearing.toFixed(0)}, pitch: ${pitch.toFixed(0)}`);

            // If map is currently moving, wait for it to stop
            if (map.current.isMoving()) {
                console.log('ActivityMap3D: Map is moving, waiting for idle...');
                await new Promise(resolve => {
                    map.current.once('idle', resolve);
                });
            }

            // Ensure map is fully loaded
            if (!map.current.loaded()) {
                console.log('ActivityMap3D: Map not fully loaded, waiting...');
                // Set a timeout to prevent infinite waiting
                await Promise.race([
                    new Promise(resolve => {
                        map.current.once('load', () => {
                            console.log('ActivityMap3D: Map loaded event fired');
                            resolve();
                        });
                    }),
                    new Promise(resolve => {
                        // Check if map becomes loaded in intervals
                        const checkInterval = setInterval(() => {
                            if (map.current && map.current.loaded()) {
                                console.log('ActivityMap3D: Map is now loaded');
                                clearInterval(checkInterval);
                                resolve();
                            }
                        }, 100);
                        // Timeout after 5 seconds
                        setTimeout(() => {
                            clearInterval(checkInterval);
                            console.log('ActivityMap3D: Timeout waiting for map to load');
                            resolve();
                        }, 5000);
                    })
                ]);
            }

            // Map is already rendering at high resolution, just capture it
            const canvas = map.current.getCanvas();
            console.log(`ActivityMap3D: Capturing map at resolution: ${canvas.width}x${canvas.height}`);

            await ensureNextRender('pre-capture');

            // Wait for multiple frames to ensure all rendering is complete
            await new Promise((resolve) => {
                let frameCount = 0;
                const waitFrames = 3; // Wait for 3 animation frames
                const frameWait = () => {
                    frameCount++;
                    if (frameCount >= waitFrames) {
                        resolve();
                    } else {
                        requestAnimationFrame(frameWait);
                    }
                };
                requestAnimationFrame(frameWait);
            });

            let captureResult = null;
            try {
                console.log('ActivityMap3D: Getting canvas for capture');
                const finalCenter = map.current.getCenter();
                const finalZoom = map.current.getZoom();
                const finalBearing = map.current.getBearing();
                const finalPitch = map.current.getPitch();
                console.log(`ActivityMap3D: Final camera at capture - center: [${finalCenter.lng.toFixed(4)}, ${finalCenter.lat.toFixed(4)}], zoom: ${finalZoom.toFixed(1)}, bearing: ${finalBearing.toFixed(0)}, pitch: ${finalPitch.toFixed(0)}`);
                console.log(`ActivityMap3D: Map was interacted with: ${hasBeenInteracted}`);

                const captureMimeType = 'image/jpeg';
                const captureQuality = 0.9; // Balance fidelity with upload size

                let blob = await captureCanvasBlob(canvas, captureMimeType, captureQuality);

                if (!blob) {
                    throw new Error('ActivityMap3D: Failed to create capture blob');
                }

                let mimeType = blob.type || captureMimeType;
                let dataUrl = await blobToDataUrl(blob);

                captureResult = {
                    dataUrl,
                    blob,
                    mimeType,
                };

                if (captureResult) {
                    setCapturedImage(dataUrl);
                    capturedImageRef.current = dataUrl;
                    console.log('ActivityMap3D: Stored captured image, blob size:', blob.size, 'bytes');
                }
            } catch (error) {
                console.error('Error during canvas capture:', error);
            }

            return captureResult;
        } catch (error) {
            console.error('Error capturing map:', error);
            return null;
        }
    }, [hasBeenInteracted, ensureNextRender]);

    useEffect(() => {
        captureCallbackRef.current = async () => {
            console.log('ActivityMap3D: Capturing current map view for backend');
            return await captureMap();
        };

        if (onCapture) {
            onCapture(() => captureCallbackRef.current());
        }
    }, [captureMap, onCapture]);

    useEffect(() => {
        if (!mapContainer.current || !activity) return;

        setIsLoading(true);
        setHasError(false);
        onRenderStartRef.current?.();
        finalRenderResolvedRef.current = false;
        hasBeenInteractedRef.current = false;
        setHasBeenInteracted(false);

        const coordinates = decodePolyline();
        if (!coordinates || coordinates.length === 0) {
            setHasError(true);
            setIsLoading(false);
            onRenderCompleteRef.current?.();
            return;
        }

        try {
            const pixelRatio = 1;

            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: style,
                center: coordinates[Math.floor(coordinates.length / 2)],
                zoom: zoom || 12,
                bearing: bearing,
                pitch: pitch,
                antialias: true,
                pixelRatio: pixelRatio, // Request high pixel ratio
                attributionControl: false,
                interactive: true,
                preserveDrawingBuffer: true
            });

            map.current.on('load', () => {
                applyReducedLabelDensity(map.current);

                const geoJSON = {
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        type: 'LineString',
                        coordinates: coordinates
                    }
                };

                map.current.addSource('route', {
                    type: 'geojson',
                    data: geoJSON
                });

                map.current.addLayer({
                    id: 'route',
                    type: 'line',
                    source: 'route',
                    layout: {
                        'line-join': 'round',
                        'line-cap': 'round'
                    },
                    paint: {
                        'line-color': '#FC4C02',
                        'line-width': buildZoomCompensatedLineWidth(lineWidth),
                        'line-opacity': 0.8
                    }
                });

                const fitBoundsToRoute = () => {
                    if (!map.current) return;

                    try {
                        const bounds = coordinates.reduce((bounds, coord) => bounds.extend(coord), new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

                        const mapWidth = map.current.getCanvas().width;
                        const mapHeight = map.current.getCanvas().height;
                        const minDimension = Math.min(mapWidth, mapHeight);

                        let adaptivePadding;
                        if (minDimension < 150) {
                            adaptivePadding = 10;
                        } else if (minDimension < 250) {
                            adaptivePadding = 15;
                        } else if (minDimension < 400) {
                            adaptivePadding = 25;
                        } else {
                            adaptivePadding = Math.min(50, Math.floor(minDimension * 0.08));
                        }

                        map.current.fitBounds(bounds, {
                            padding: adaptivePadding,
                            bearing: bearing,
                            pitch: pitch
                        });
                    } catch (error) {
                        console.error('ActivityMap3D: Error fitting bounds:', error);

                        const centerCoord = coordinates[Math.floor(coordinates.length / 2)];
                        if (centerCoord && !isNaN(centerCoord[0]) && !isNaN(centerCoord[1])) {
                            map.current.jumpTo({
                                center: centerCoord,
                                zoom: zoom || 13,
                                bearing: bearing,
                                pitch: pitch
                            });
                        }
                    }
                    setIsLoading(false);
                    scheduleFinalRenderCheck();
                };

                map.current.once('idle', fitBoundsToRoute);
            });

            const updateCamera = () => {
                if (!map.current) return;
                const center = map.current.getCenter();
                const currentZoom = map.current.getZoom();
                const currentBearing = map.current.getBearing();
                const currentPitch = map.current.getPitch();

                setCameraInfo({
                    zoom: currentZoom.toFixed(1),
                    bearing: Math.round(currentBearing),
                    pitch: Math.round(currentPitch),
                    lng: center.lng.toFixed(4),
                    lat: center.lat.toFixed(4)
                });
            };

            const handleInteraction = () => {
                if (!hasBeenInteractedRef.current) {
                    hasBeenInteractedRef.current = true;
                    setHasBeenInteracted(true);
                }
                updateCamera();
                if (mapInteractionCallbackRef.current) {
                    mapInteractionCallbackRef.current(() => captureCallbackRef.current());
                }
            };

            map.current.on('move', handleInteraction);
            map.current.on('moveend', handleInteraction);

            map.current.on('error', (e) => {
                console.error('Map error:', e);
                setHasError(true);
                setIsLoading(false);
                onRenderCompleteRef.current?.();
            });

        } catch (error) {
            console.error('Error initializing map:', error);
            setHasError(true);
            setIsLoading(false);
            onRenderCompleteRef.current?.();
        }

        return () => {
            if (map.current) {
                map.current.remove();
                map.current = null;
            }
        };
    }, [activity, style, bearing, pitch, zoom, lineWidth, highDPI, decodePolyline, scheduleFinalRenderCheck]);

    if (!activity) {
        return (
            <div style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f0f0', borderRadius: '8px' }}>
                <span style={{ color: '#666' }}>No activity selected</span>
            </div>
        );
    }

    if (hasError) {
        return (
            <div style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff3cd', borderRadius: '8px' }}>
                <span style={{ color: '#856404' }}>Error loading 3D map</span>
            </div>
        );
    }

    // Use display dimensions if provided, otherwise use render dimensions
    const containerWidth = displayWidth || width;
    const containerHeight = displayHeight || height;

    return (
        <div style={{
            position: 'relative',
            width: containerWidth,
            height: containerHeight,
            overflow: 'hidden'
        }}>
            {/* Main map container - render at full resolution but scale down visually */}
            <div style={{
                position: 'relative',
                width: width,
                height: height,
                transform: displayWidth ? `scale(${containerWidth / width})` : undefined,
                transformOrigin: 'top left',
                borderRadius: '8px',
                overflow: 'hidden'
            }}>
                <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />

                {isLoading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)'
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ marginBottom: '10px' }}>Loading 3D map...</div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                                {activity.name || 'Activity'}
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default ActivityMap3D;
